/**
 * 消息列表组件
 */
import { renderMarkdown, processMessageContent } from '../utils/markdown.js';
import { formatDateTime } from '../utils/ui.js';
import type { Message } from '../api/types.js';

export interface MessageListOptions {
  onLoadHistory: (before?: string) => Promise<void>;
}

export class MessageList {
  private options: MessageListOptions;
  private container: HTMLElement | null = null;
  private messages: Map<string, HTMLElement> = new Map();
  private oldestMessageTimestamp: string | null = null;
  private isLoadingMore = false;

  constructor(options: MessageListOptions) {
    this.options = options;
  }

  /**
   * 初始化组件
   */
  private init(): void {
    this.container = document.getElementById('messages');
    if (!this.container) {
      console.error('Messages container not found');
      return;
    }
  }

  /**
   * 设置所有消息（替换现有消息）
   * @param messages 消息列表
   * @param scrollToBottom 是否滚动到底部（默认为 false）
   */
  setMessages(messages: Message[], scrollToBottom = false): void {
    this.init();

    if (!this.container) return;

    // 清空现有消息
    this.container.innerHTML = '';
    this.messages.clear();

    // 添加新消息
    messages.forEach((msg) => {
      this.addMessageToDOM(msg);
    });

    // 更新最旧消息时间戳
    if (messages.length > 0) {
      this.oldestMessageTimestamp = messages[messages.length - 1].timestamp;
    }

    // 如果需要滚动到底部
    if (scrollToBottom && messages.length > 0) {
      // 使用 requestAnimationFrame 确保 DOM 渲染完成
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.container) {
            this.container.scrollTop = this.container.scrollHeight;
          }
        });
      });
    }
  }

  /**
   * 添加一条消息
   */
  addMessage(sender: 'user' | 'assistant', content: string, timestamp: string, senderName?: string, model?: string): void {
    this.init();

    const msg: Message = {
      id: `temp_${Date.now()}`,
      chat_jid: '',
      sender: sender,
      sender_name: sender === 'user' ? '你' : (senderName || '助手'),
      content,
      timestamp,
      is_from_me: sender === 'user',
      is_bot_message: sender === 'assistant',
      model,
    };

    this.addMessageToDOM(msg);
  }

  /**
   * 添加消息到 DOM
   */
  private addMessageToDOM(msg: Message): void {
    if (!this.container) return;

    const messageDiv = this.createMessageElement(msg);
    this.container.appendChild(messageDiv);

    // 存储消息引用
    this.messages.set(msg.id, messageDiv);

    // 处理消息内容（Markdown、代码块等）
    const textDiv = messageDiv.querySelector('.message-text');
    if (textDiv) {
      processMessageContent(textDiv as HTMLElement);
    }
  }

  /**
   * 创建消息元素
   */
  private createMessageElement(msg: Message): HTMLElement {
    const messageDiv = document.createElement('div');
    messageDiv.className = 'message';
    messageDiv.dataset.sender = msg.is_from_me ? 'user' : 'assistant';
    messageDiv.dataset.id = msg.id;

    // 头像
    const avatarDiv = document.createElement('div');
    avatarDiv.className = `message-avatar ${msg.is_from_me ? 'user' : 'assistant'}`;
    avatarDiv.textContent = msg.is_from_me ? '👤' : '🤖';

    // 内容容器
    const contentDiv = document.createElement('div');
    contentDiv.className = 'message-content';

    // 头部（发送者 + 模型 + 时间）
    const headerDiv = document.createElement('div');
    headerDiv.className = 'message-header';

    const senderSpan = document.createElement('span');
    senderSpan.className = 'message-sender';
    senderSpan.textContent = msg.is_from_me ? '你' : '助手';
    headerDiv.appendChild(senderSpan);

    // 添加模型标签（仅助手消息，放在"助手"后面）
    if (!msg.is_from_me && msg.model) {
      const modelSpan = document.createElement('span');
      modelSpan.className = 'message-model';
      modelSpan.textContent = this.formatModelName(msg.model);
      headerDiv.appendChild(modelSpan);
    }

    const timeSpan = document.createElement('span');
    timeSpan.className = 'message-time';
    timeSpan.textContent = formatDateTime(msg.timestamp);
    headerDiv.appendChild(timeSpan);

    // 文本内容
    const textDiv = document.createElement('div');
    textDiv.className = 'message-text';

    // 渲染 Markdown（所有消息都支持 markdown，包括用户上传的图片）
    textDiv.innerHTML = renderMarkdown(msg.content);

    contentDiv.appendChild(headerDiv);
    contentDiv.appendChild(textDiv);

    messageDiv.appendChild(avatarDiv);
    messageDiv.appendChild(contentDiv);

    // 如果是用户消息，添加处理状态指示器
    if (msg.is_from_me) {
      const statusDiv = document.createElement('div');
      statusDiv.className = 'message-status';
      statusDiv.dataset.messageId = msg.id;
      contentDiv.appendChild(statusDiv);
    }

    return messageDiv;
  }

  /**
   * 设置消息的处理状态
   * @param messageId 消息ID
   * @param status 状态：'running' | 'success' | 'error'
   * @param message 状态消息（可选）
   */
  setMessageStatus(messageId: string, status: 'running' | 'success' | 'error', message?: string): void {
    // 查找消息元素
    const messageDiv = this.messages.get(messageId);
    if (!messageDiv) {
      // 如果是临时消息，查找最新的用户消息
      const latestUserMessage = this.container?.querySelector('.message[data-sender="user"]:last-child');
      if (latestUserMessage) {
        this.updateMessageStatus(latestUserMessage as HTMLElement, status, message);
      }
      return;
    }

    this.updateMessageStatus(messageDiv, status, message);
  }

  /**
   * 更新消息的处理状态
   */
  private updateMessageStatus(messageDiv: HTMLElement, status: 'running' | 'success' | 'error', message?: string): void {
    const statusDiv = messageDiv.querySelector('.message-status') as HTMLElement;
    if (!statusDiv) return;

    statusDiv.style.display = 'flex';
    statusDiv.className = `message-status ${status}`;

    switch (status) {
      case 'running':
        // 显示细进度条在消息状态区域
        statusDiv.innerHTML = '<div class="message-progress-bar"></div>';
        break;
      case 'success':
        statusDiv.innerHTML = '<span class="status-icon">✓</span> 已回复';
        break;
      case 'error':
        statusDiv.innerHTML = `<span class="status-icon">✕</span> ${message || '处理失败'}`;
        break;
    }
  }

  /**
   * 格式化模型名称，使其更简洁易读
   */
  private formatModelName(model: string): string {
    // 移除提供商前缀，只显示模型名称
    return model
      .replace(/^anthropic\//, '')
      .replace(/^openai\//, '')
      .replace(/^google\//, '')
      .replace(/^meta-llama\//, '')
      .replace(/^mistralai\//, '')
      .replace(/^x-ai\//, '')
      .replace(/-sonnet-.*/, '-sonnet')
      .replace(/-opus-.*/, '-opus')
      .replace(/-haiku-.*/, '-haiku')
      .replace(/(\d{4})-(\d{2})-(\d{2})/, '$1$2$3') // 紧凑日期格式
      || model;
  }

  /**
   * 加载更多历史消息
   */
  async loadMore(): Promise<void> {
    if (this.isLoadingMore || !this.oldestMessageTimestamp) {
      return;
    }

    this.isLoadingMore = true;
    const loadingIndicator = this.showLoadingIndicator();

    // 保存当前滚动位置和高度
    const prevScrollTop = this.container?.scrollTop || 0;
    const prevScrollHeight = this.container?.scrollHeight || 0;

    try {
      await this.options.onLoadHistory(this.oldestMessageTimestamp);

      // 等待 DOM 更新后恢复滚动位置
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.container) {
            const newScrollHeight = this.container.scrollHeight;
            // 计算新增内容的高度，并调整滚动位置
            this.container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
          }
        });
      });
    } finally {
      this.isLoadingMore = false;
      loadingIndicator?.remove();
    }
  }

  /**
   * 在消息列表前面添加消息（用于加载更多历史）
   */
  prependMessages(newMessages: Message[]): void {
    if (!this.container || newMessages.length === 0) return;

    // 保存当前滚动位置和高度
    const prevScrollHeight = this.container.scrollHeight;
    const prevScrollTop = this.container.scrollTop;

    // 按时间正序添加（最旧的在前面）
    const sortedMessages = [...newMessages].sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // 添加到 DOM 最前面
    sortedMessages.forEach((msg) => {
      // 跳过已存在的消息
      if (this.messages.has(msg.id)) return;

      const messageDiv = this.createMessageElement(msg);
      this.container?.insertBefore(messageDiv, this.container.firstChild);
      this.messages.set(msg.id, messageDiv);

      // 处理消息内容（Markdown、代码块等）
      const textDiv = messageDiv.querySelector('.message-text');
      if (textDiv) {
        processMessageContent(textDiv as HTMLElement);
      }
    });

    // 更新最旧消息时间戳
    if (sortedMessages.length > 0) {
      const oldest = sortedMessages[0];
      if (!this.oldestMessageTimestamp || oldest.timestamp < this.oldestMessageTimestamp) {
        this.oldestMessageTimestamp = oldest.timestamp;
      }
    }

    // 恢复滚动位置：保持用户之前的阅读位置
    requestAnimationFrame(() => {
      if (this.container) {
        const newScrollHeight = this.container.scrollHeight;
        this.container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
      }
    });
  }

  /**
   * 显示加载指示器
   */
  private showLoadingIndicator(): HTMLElement | null {
    if (!this.container) return null;

    const existing = this.container.querySelector('.loading-indicator');
    if (existing) return existing as HTMLElement;

    const indicator = document.createElement('div');
    indicator.className = 'loading-indicator';
    indicator.textContent = '加载更多消息...';
    indicator.style.cssText = `
      text-align: center;
      padding: var(--spacing-md);
      color: var(--color-text-muted);
      font-size: 0.875rem;
    `;

    this.container.insertBefore(indicator, this.container.firstChild);
    return indicator;
  }

  /**
   * 隐藏加载指示器
   */
  private hideLoadingIndicator(): void {
    if (!this.container) return;

    const indicator = this.container.querySelector('.loading-indicator');
    if (indicator) {
      indicator.remove();
    }
  }

  /**
   * 滚动到指定位置（用于加载更多后恢复滚动位置）
   */
  restoreScrollPosition(scrollTop: number): void {
    if (this.container) {
      this.container.scrollTop = scrollTop;
    }
  }

  /**
   * 获取当前滚动位置
   */
  getScrollPosition(): number {
    return this.container?.scrollTop ?? 0;
  }

  /**
   * 清空所有消息
   */
  clear(): void {
    if (this.container) {
      this.container.innerHTML = '';
    }
    this.messages.clear();
    this.oldestMessageTimestamp = null;
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    this.clear();
    this.container = null;
  }
}
